import { useCallback, useEffect, useState } from "react";

import { EnumOptionsBE } from "src/api/flowTypes";
import { DesiredType } from "src/api/types";
import { JSONValue } from "src/datasets/DatasetTable/types";
import {
  jsonToPon,
  ponToJson,
  validateCellValue,
} from "src/datasets/DatasetTable/utils";
import { ValidationResult } from "src/datasets/DatasetTable/validators";
import { logger } from "src/utils/logger";

const forgivingStringify = (value: JSONValue | undefined) =>
  JSON.stringify(value) ?? "";

export const useCellLocalValue = (
  receivedValue: JSONValue | undefined,
  onSave: (value: JSONValue) => void,
  params: {
    desiredType: DesiredType;
    nullable: boolean;
    required: boolean;
    enumValues: EnumOptionsBE | null | undefined;
  },
): {
  value: string;
  error: ValidationResult | undefined;
  setError: (error: ValidationResult | undefined) => void;
  validateAndSave: (value: string) => void;
  setLocalValue: (value: string) => void;
} => {
  const { desiredType, nullable, required, enumValues } = params;

  const [value, setValue] = useState(() =>
    jsonToPon(forgivingStringify(receivedValue)),
  );
  const [error, setError] = useState<ValidationResult | undefined>(undefined);

  useEffect(() => {
    setValue(jsonToPon(forgivingStringify(receivedValue)));
  }, [receivedValue]);

  useEffect(() => {
    setError(
      validateCellValue(ponToJson(value), {
        type: desiredType,
        nullable,
        required,
        enumValues,
      }),
    );
    // We're checking value and enumValues as strings
    // enumValues do not change actually, but to make sure we don't have
    // unnecessary re-renders we convert them to string
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredType, nullable, required, value, String(enumValues)]);

  const validateAndSave = useCallback(
    (ponValue: string) => {
      const jsonValue = ponToJson(ponValue);
      const error = validateCellValue(jsonValue, {
        type: desiredType,
        nullable,
        required,
        enumValues,
      });
      setError(error);

      try {
        onSave(jsonValue.trim() === "" ? undefined : JSON.parse(jsonValue));
        setValue(jsonToPon(jsonValue));
      } catch (e) {
        setError(
          error ?? {
            message: "Invalid JSON",
          },
        );
        logger.log(e);
      }
    },
    [desiredType, nullable, required, enumValues, onSave],
  );

  return {
    value,
    error,
    setError,
    validateAndSave: validateAndSave,
    setLocalValue: setValue,
  };
};
